import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { scrollToSection } from "../lib/scroll";

const Navbar = () => {
  const { lang, setLang, t, cartCount, setCartOpen } = useShop();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.catalog, href: "/urunler" },
    { label: t.nav.values, href: "#values" },
    { label: t.nav.b2b, href: "#b2b" },
    { label: t.nav.story, href: "#story" },
  ];

  const go = (href) => {
    setMobileOpen(false);
    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToSection(href), 700);
      } else {
        scrollToSection(href);
      }
    } else {
      navigate(href);
      window.scrollTo(0, 0);
    }
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled
          ? "bg-cream/70 backdrop-blur-xl border-brown/10 shadow-[0_1px_24px_rgba(90,56,30,0.06)]"
          : "bg-transparent border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={goHome}
          className="flex flex-col items-start leading-none group"
          data-testid="nav-logo"
        >
          <span className="font-serif text-2xl md:text-[28px] font-medium tracking-tight text-brown group-hover:text-olive transition-colors duration-300">
            VLP Dolap
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-brown-muted mt-1">
            Volpar
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-sm tracking-wide text-brown-light hover:text-brown relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-olive after:transition-[width] after:duration-300 hover:after:w-full transition-colors duration-300"
              data-testid={`nav-link-${l.href.replace(/[#/]/g, "")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div
            className="flex items-center border border-brown/15 rounded-full p-0.5"
            data-testid="lang-switcher"
          >
            {["tr", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full text-xs font-medium tracking-widest uppercase transition-colors duration-300 ${
                  lang === l ? "bg-brown text-cream" : "text-brown-light hover:text-brown"
                }`}
                data-testid={`lang-${l}-button`}
              >
                {l}
              </button>
            ))}
          </div>

          <Link
            to={user ? (user.role === "admin" ? "/admin" : "/hesap") : "/giris"}
            className="p-2 text-brown hover:text-olive transition-colors duration-300"
            aria-label="Account"
            data-testid="account-link"
          >
            <User size={22} strokeWidth={1.5} />
          </Link>

          <button
            onClick={() => setCartOpen(true)}
            className="relative p-2 text-brown hover:text-olive transition-colors duration-300"
            aria-label="Cart"
            data-testid="cart-open-button"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            <AnimatePresence>
              {cartCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-olive text-cream text-[10px] font-semibold flex items-center justify-center"
                  data-testid="cart-count-badge"
                >
                  {cartCount}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <button
            onClick={() => go("/urunler")}
            className="hidden md:inline-flex px-6 py-2.5 rounded-full bg-olive text-cream text-sm font-medium tracking-wide hover:bg-olive-dark transition-colors duration-300"
            data-testid="nav-order-button"
          >
            {t.nav.order}
          </button>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 text-brown"
            aria-label="Menu"
            data-testid="mobile-menu-button"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-t border-brown/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left font-serif text-2xl text-brown"
                  data-testid={`mobile-nav-link-${l.href.replace(/[#/]/g, "")}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
